import {
  exportProducts,
  generateProductDescription,
  generateProductDescriptionBulk,
  generateProductKeywords,
  generateProductKeywordsBulk,
  generateProductThema,
  generateProductThemaBulk,
  createProductFromManuscript,
  ManuscriptUploadRequest,
  generateProductManuscriptAnalysis,
  generateProductSubtitle,
  deleteProduct,
  productsSearch,
  ProductSearchRequest,
  updateProductFinalKeywords,
  getProductRaw,
  getProductAcpFeedPreview,
  getProductAmplifyAPlusContent,
  listProductsAmplifySummaries,
  type ProductAmplifySummary,
  upsertProductKeywordFeedback,
  type KeywordFeedbackRequest,
} from "reedy-data/api";
import { FirestoreProductSubject } from "reedy-data/models";
import { BaseApi } from "./base";

export interface DescriptionType {
  textType: string;
  audience: string;
}

export class ProductsApi extends BaseApi {
  async exportProducts(ids: string[]) {
    if (ids.length === 0) {
      throw new Error("Product IDs array cannot be empty");
    }

    const response = await this.request(exportProducts, {
      body: { productIds: ids },
    });

    return response?.data;
  }

  async generateDescription(
    productId: string,
    options: {
      hash: string;
      targetGroup: string | null;
      specialRequest: string | null;
    },
  ) {
    const response = await this.request(generateProductDescription, {
      path: { product_id: productId },
      body: {
        hash: options.hash,
        targetGroup: options.targetGroup,
        specialRequest: options.specialRequest,
      },
    });

    return response?.data;
  }

  async generateDescriptionsBulk(
    productIds: string[],
    descriptionTypes: DescriptionType[],
  ) {
    const response = await this.request(generateProductDescriptionBulk, {
      body: { identifiers: productIds, filters: descriptionTypes },
    });

    return response?.data;
  }

  async generateKeywords(productId: string) {
    const response = await this.request(generateProductKeywords, {
      path: { product_id: productId },
      body: {
        generate: true,
        rank: true,
      },
    });

    return response?.data;
  }

  async generateKeywordsBulk(productIds: string[]) {
    const response = await this.request(generateProductKeywordsBulk, {
      body: {
        generate: true,
        identifiers: productIds,
      },
    });

    return response?.data;
  }

  async generateThema(productId: string) {
    const response = await this.request(generateProductThema, {
      path: { product_id: productId },
    });

    return response?.data;
  }

  async generateThemaBulk(productIds: string[]) {
    const response = await this.request(generateProductThemaBulk, {
      body: { identifiers: productIds },
    });

    return response?.data;
  }

  async generateSubtitle(productId: string, specialRequest: string | null) {
    const response = await this.request(generateProductSubtitle, {
      path: { product_id: productId },
      body: {
        specialRequest,
      },
    });

    return response?.data;
  }

  async createProductFromManuscript(data: ManuscriptUploadRequest) {
    const response = await this.request(createProductFromManuscript, {
      body: data,
    });

    return response?.data;
  }

  async generateProductManuscriptAnalysis(productId: string) {
    const response = await this.request(generateProductManuscriptAnalysis, {
      path: { product_id: productId },
    });

    return response?.data;
  }

  async deleteProduct(productId: string) {
    const response = await this.request(deleteProduct, {
      path: { product_id: productId },
    });

    return response?.data;
  }

  async searchProducts(params: ProductSearchRequest) {
    const response = await this.request(productsSearch, {
      body: params,
    });

    return response?.data;
  }

  async updateFinalKeywords(
    productId: string,
    subjects: FirestoreProductSubject[],
  ) {
    await this.request(updateProductFinalKeywords, {
      path: { product_id: productId },
      body: { subjects },
    });
  }

  async getProductRaw(productId: string) {
    const response = await this.request(getProductRaw, {
      path: { product_id: productId },
    });

    return response?.data;
  }

  async getProductAcpFeedPreview(productId: string) {
    const response = await this.request(getProductAcpFeedPreview, {
      path: { product_id: productId },
    });

    return response?.data;
  }

  async getAmplifyAPlusContent(productId: string) {
    // A 403 says A+ content is not enabled for the account, so it is a state to
    // render rather than a failure to report, retry and log.
    const response = await this.request(
      getProductAmplifyAPlusContent,
      { path: { product_id: productId } },
      ({ status }) => status !== 403,
    );

    return response?.data ?? null;
  }

  async listAmplifySummaries(
    productIds: string[],
  ): Promise<ProductAmplifySummary[]> {
    const response = await this.request(listProductsAmplifySummaries, {
      body: { productIds },
    });

    return response?.data?.products ?? [];
  }

  async upsertKeywordFeedback(
    productId: string,
    feedbackId: string,
    body: KeywordFeedbackRequest,
  ) {
    await this.request(upsertProductKeywordFeedback, {
      path: { product_id: productId, feedback_id: feedbackId },
      body,
    });
  }
}
